/**
 * The script's events as buttons, grouped by act and offered in act order. Each
 * happens now; done/disabled states come from the store so a beat cannot be
 * fired twice by accident and a beat whose cause has not happened yet stays
 * grey. Acts 4 and 6 have no presenter events, and none of the Reserve pieces
 * needs one.
 *
 * The whole 30-minute run is drivable from this list plus the Script screens
 * above it — nothing in the run of show needs a typed URL or a console.
 */
import { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { addWorkingDaysToKey, dateKeyStart, formatInBranchZone } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { cutoffTime, roundDateFor, roundFired, roundFor, roundSendTime, selectConfirmationRows } from "../engine/confirmationRound";
import { useBranchClock } from "../components/branchClock";
import { useDemoState } from "../store/useDemoStore";
import * as actions from "./actions";

type GroupKey = "act1" | "act2" | "act3" | "act5";

interface EventDef {
  key: string;
  group: GroupKey;
  label: string;
  step: string;
  run: () => Promise<unknown>;
  done: boolean;
  enabled: boolean;
}

/** Act order, top to bottom. */
export const EVENT_GROUPS: { key: GroupKey; label: string }[] = [
  { key: "act1", label: "Act 1 · Referral to first visit" },
  { key: "act2", label: "Act 2 · The patient" },
  { key: "act3", label: "Act 3 · The clinician's week" },
  { key: "act5", label: "Act 5 · The command center" },
];

export function EventButtons({ disabled }: { disabled: boolean }) {
  const s = useDemoState();
  // The two 3.3 labels carry a clock time, so they read it the way the rest of the
  // product does: the user's own twelve/twenty-four-hour setting, through the
  // product's own helper.
  const clock = useBranchClock(s.config.branch.timeZone);
  const branchClock = (hhmm: string) => clock.wall(hhmm);
  const [running, setRunning] = useState<string | undefined>();
  const alvarez = s.referrals["ref-alvarez"];
  const alvarezHold = Object.values(s.holds).find((h) => h.patientRef === "alvarez");
  const okaforHold = Object.values(s.holds).find((h) => h.patientRef === "okafor");
  const alvarezSlipsTo = alvarez ? formatInBranchZone(dateKeyStart(addWorkingDaysToKey(alvarez.expectedDischarge, alvarez.dischargeChangedAtIso ? 0 : 1), s.config.branch.timeZone), s.config.branch.timeZone, "EEEE") : "the next day";
  const okaforReady = okaforHold ? s.readiness[okaforHold.id] ?? {} : {};
  // Act 2: the plan events. Each greys out on its fired key; each is offered only while its cause is still true.
  const admittedMonday = Object.values(s.episodes).find((e) => e.patientRef === "census-01");
  const ptPending = !!admittedMonday?.pendingDisciplines.some((p) => p.discipline === "PT");
  const ptOrdersFired = s.presenter.fired.includes(`track:${admittedMonday?.id}:PT`);
  // 3.3 reads the confirmation round: the round has to have gone before the replies can land.
  const danaId = s.meta.bindings.providerIdsByHandle.dana;
  const tomorrow = roundDateFor(s, new Date());
  const danaRound = roundFor(s, danaId, tomorrow);
  const roundRows = danaId === undefined ? [] : selectConfirmationRows(s, danaId, tomorrow);
  const roundHasGone = roundFired(danaRound);
  const cutoffPassed = !!danaRound.cutoffAtIso;

  const events: EventDef[] = [
    // --- Act 1 ---------------------------------------------------------------
    { key: "arrived", group: "act1", label: "1.1 Referral arrives (Alvarez)", step: "1.1", run: async () => actions.referralArrived("ref-alvarez"), done: !!alvarez && alvarez.status !== "pending", enabled: alvarez?.status === "pending" },
    { key: "accept", group: "act1", label: "1.2 Accept and place hold (Alvarez)", step: "1.2", run: () => actions.acceptReferral("ref-alvarez"), done: !!alvarezHold, enabled: alvarez?.status === "new" },
    { key: "discharge-slips", group: "act1", label: `1.3 Discharge slips (Alvarez → ${alvarezSlipsTo})`, step: "1.3", run: async () => actions.dischargeDateChanged("ref-alvarez"), done: !!alvarez?.dischargeChangedAtIso, enabled: !!alvarezHold && !alvarez?.dischargeChangedAtIso },
    { key: "welcome-okafor", group: "act1", label: "1.4 Welcome call completes (Okafor) → converts", step: "1.4", run: () => actions.welcomeCallCompleted("okafor"), done: okaforReady.readinessCall === "done", enabled: !!okaforHold && okaforReady.readinessCall !== "done" },

    // --- Act 2 ---------------------------------------------------------------
    { key: "pt-orders", group: "act2", label: "2.1 PT orders arrive (Whitfield, 2 a week × 4 wks)", step: "2.1", run: async () => actions.ptOrdersArrive("census-01"), done: ptOrdersFired, enabled: !!admittedMonday && ptPending && !ptOrdersFired },

    // --- Act 3 ---------------------------------------------------------------
    { key: "round", group: "act3", label: `3.3 ${branchClock(roundSendTime(s, danaRound))}: the round goes (Dana)`, step: "3.3", run: async () => actions.fireConfirmationRound("dana", tomorrow), done: roundHasGone, enabled: !roundHasGone && roundRows.length > 0 },
    { key: "replies", group: "act3", label: `3.3 ${branchClock(cutoffTime(s))}: replies land (Dana)`, step: "3.3", run: async () => actions.confirmationReplies("dana", "Whitfield", "afternoon?", tomorrow), done: cutoffPassed, enabled: roundHasGone && !cutoffPassed },

    // --- Act 5 ---------------------------------------------------------------
    { key: "callout", group: "act5", label: "5.2 Call-out: Marcus (shift canceled for real)", step: "5.2", run: () => actions.callOutNow("marcus"), done: !!s.presenter.callOut, enabled: !s.presenter.callOut },
    { key: "restore", group: "act5", label: "5.2 Restore Marcus's shift", step: "5.2", run: () => actions.restoreCallOut(), done: false, enabled: !!s.presenter.callOut },
  ];

  const fire = async (e: EventDef) => {
    setRunning(e.key);
    try {
      await e.run();
    } catch {
      // logged by the action
    } finally {
      setRunning(undefined);
    }
  };

  const renderButton = (e: EventDef) => (
    <Button
      key={e.key}
      size="small"
      variant={e.done ? "text" : "outlined"}
      color={e.done ? "success" : "primary"}
      disabled={disabled || !e.enabled || running !== undefined}
      onClick={() => fire(e)}
      sx={{ justifyContent: "flex-start", textTransform: "none" }}
    >
      {running === e.key ? "…" : e.done ? "✓ " : ""}
      {e.label}
    </Button>
  );

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Events (happen now)
      </Typography>
      <Stack spacing={1.25}>
        {EVENT_GROUPS.map((g) => (
          <Box key={g.key}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {g.label}
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.25 }}>
              {events.filter((e) => e.group === g.key).map(renderButton)}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
